// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Stats Tab Component
 * 
 * Displays ship statistics with mini-charts and progress bars
 * Extracted from InfoPanel for better code organization
 */

import React, { useState, useEffect } from 'react';
import { useCargo } from '../../contexts/CargoContext';
import { useCredits } from '../../contexts/CreditsContext';
import MiniChart from '../MiniChart';
import tradeGoods from '../../config/TradeGoodsConfig';

const StatsTab = () => {
  const { cargo, cargoCapacity } = useCargo();
  const { credits } = useCredits();

  // Helper function to convert cargo to array
  const getCargoArray = () => {
    if (!cargo || typeof cargo !== 'object') return [];
    
    return Object.entries(cargo).map(([goodId, quantity]) => {
      const good = tradeGoods[goodId];
      if (!good) return null;
      
      return {
        id: goodId,
        name: good.name,
        icon: good.icon,
        quantity: quantity,
        weight: good.weight,
        basePrice: good.basePrice
      };
    }).filter(Boolean);
  };

  // Helper function to get cargo used
  const getCargoUsed = () => {
    const cargoArray = getCargoArray();
    return cargoArray.reduce((sum, item) => sum + (item.quantity * item.weight), 0);
  };

  // Historical data for charts (last 10 data points)
  const [creditsHistory, setCreditsHistory] = useState([1000, 1050, 980, 1100, 1200, 1150, 1300, 1250, 1400, credits]);
  const [cargoHistory, setCargoHistory] = useState([0, 5, 10, 15, 12, 8, 15, 20, 18, 15]);
  const [jumpsHistory] = useState([0, 1, 2, 3, 4, 5, 6, 7, 8, 9]);

  // Update history when values change
  useEffect(() => {
    setCreditsHistory(prev => [...prev.slice(-9), credits]);
  }, [credits]);

  useEffect(() => {
    const currentCargo = getCargoUsed();
    setCargoHistory(prev => [...prev.slice(-9), currentCargo]);
  }, [cargo]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="tab-content">
      {/* Primary Stats Grid with Charts */}
      <div className="stats-section">
        <div className="section-title">SHIP STATUS</div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <div className="stat-label">CREDITS</div>
              <div className="stat-value">{credits.toLocaleString()}</div>
              <div className="stat-chart">
                <MiniChart 
                  data={creditsHistory} 
                  width={80} 
                  height={20} 
                  color="#00ff88"
                  fillColor="rgba(0, 255, 136, 0.1)"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-info">
              <div className="stat-label">CARGO</div>
              <div className="stat-value">{getCargoUsed()}/{cargoCapacity || 0}u</div>
              <div className="stat-chart">
                <MiniChart 
                  data={cargoHistory} 
                  width={80} 
                  height={20} 
                  color="#00ffff"
                  fillColor="rgba(0, 255, 255, 0.1)"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-info">
              <div className="stat-label">POWER</div>
              <div className="stat-value">100%</div>
              <div className="stat-chart">
                <MiniChart 
                  data={[100, 100, 98, 100, 100, 99, 100, 100, 100, 100]} 
                  width={80} 
                  height={20} 
                  color="#ffaa00"
                  fillColor="rgba(255, 170, 0, 0.1)"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🛡️</div>
            <div className="stat-info">
              <div className="stat-label">SHIELDS</div>
              <div className="stat-value">100%</div>
              <div className="stat-chart">
                <MiniChart 
                  data={[100, 95, 90, 95, 100, 100, 98, 100, 100, 100]} 
                  width={80} 
                  height={20} 
                  color="#ff00ff"
                  fillColor="rgba(255, 0, 255, 0.1)"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Stats with Charts */}
      <div className="stats-section">
        <div className="section-title">MISSION LOG</div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-info">
              <div className="stat-label">MISSIONS</div>
              <div className="stat-value">0</div>
              <div className="stat-chart">
                <MiniChart 
                  data={[0, 0, 0, 0, 0, 0, 0, 0, 0, 0]} 
                  width={80} 
                  height={20} 
                  color="#00ffff"
                  fillColor="rgba(0, 255, 255, 0.1)"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚔️</div>
            <div className="stat-info">
              <div className="stat-label">KILLS</div>
              <div className="stat-value">0</div>
              <div className="stat-chart">
                <MiniChart 
                  data={[0, 0, 0, 0, 0, 0, 0, 0, 0, 0]} 
                  width={80} 
                  height={20} 
                  color="#ff4444"
                  fillColor="rgba(255, 68, 68, 0.1)"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🌟</div>
            <div className="stat-info">
              <div className="stat-label">SYSTEMS</div>
              <div className="stat-value">1/16</div>
              <div className="stat-chart">
                <MiniChart 
                  data={[0, 0, 0, 0, 0, 0, 0, 0, 0, 1]} 
                  width={80} 
                  height={20} 
                  color="#ffaa00"
                  fillColor="rgba(255, 170, 0, 0.1)"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🚀</div>
            <div className="stat-info">
              <div className="stat-label">JUMPS</div>
              <div className="stat-value">0</div>
              <div className="stat-chart">
                <MiniChart 
                  data={jumpsHistory} 
                  width={80} 
                  height={20} 
                  color="#00ff88"
                  fillColor="rgba(0, 255, 136, 0.1)"
                  strokeWidth={1.5}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bars */}
      <div className="stats-section">
        <div className="section-title">EXPLORATION</div>
        <div className="progress-stats">
          <div className="progress-stat">
            <div className="progress-stat-header">
              <span className="progress-stat-label">Systems Discovered</span>
              <span className="progress-stat-value">1/16</span>
            </div>
            <div className="progress-stat-bar">
              <div className="progress-stat-fill" style={{ width: '6.25%', background: '#00ffff' }}></div>
            </div>
          </div>
          <div className="progress-stat">
            <div className="progress-stat-header">
              <span className="progress-stat-label">Combat Victories</span>
              <span className="progress-stat-value">0/10</span>
            </div>
            <div className="progress-stat-bar">
              <div className="progress-stat-fill" style={{ width: '0%', background: '#ff00ff' }}></div>
            </div>
          </div>
          <div className="progress-stat">
            <div className="progress-stat-header">
              <span className="progress-stat-label">Trade Profit</span>
              <span className="progress-stat-value">0 ₡</span>
            </div>
            <div className="progress-stat-bar">
              <div className="progress-stat-fill" style={{ width: '0%', background: '#00ff88' }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsTab;
